import React, { useState } from 'react';
import { ethers } from 'ethers';

const TokenBatchTool = () => {
  // 模式选择：'transfer'、'sweep' 或 'create'
  const [mode, setMode] = useState('transfer');
  
  // 转账方式：'wallet' 或 'privateKey'
  const [transferMethod, setTransferMethod] = useState('wallet');
  
  // 通用状态
  const [tokenAddress, setTokenAddress] = useState('');
  const [tokenInfo, setTokenInfo] = useState(null); // 存储代币信息
  const [isProcessing, setIsProcessing] = useState(false);
  const [txHashes, setTxHashes] = useState([]);
  
  // 归集结果状态
  const [sweepResults, setSweepResults] = useState([]);
  
  // 转账模式状态
  const [recipients, setRecipients] = useState([{ address: '', amount: '' }]);
  const [privateKey, setPrivateKey] = useState('');
  const [bulkAddresses, setBulkAddresses] = useState(''); // 批量地址输入
  const [uniformAmount, setUniformAmount] = useState(''); // 统一金额设置
  
  // 归集模式状态
  const [privateKeys, setPrivateKeys] = useState('');
  const [destinationAddress, setDestinationAddress] = useState('');
  const [sweepAmount, setSweepAmount] = useState(''); // 新增归集数量字段

  // 批量创建钱包状态
  const [walletCount, setWalletCount] = useState(5);
  const [createdWallets, setCreatedWallets] = useState([]);

  // 添加接收者
  const addRecipient = () => {
    setRecipients([...recipients, { address: '', amount: '' }]);
  };

  // 更新接收者信息
  const updateRecipient = (index, field, value) => {
    const newRecipients = [...recipients];
    newRecipients[index][field] = value;
    setRecipients(newRecipients);
  };

  // 处理批量地址
  const processBulkAddresses = () => {
    if (!bulkAddresses.trim()) {
      alert('请输入要批量添加的地址');
      return;
    }

    // 分割地址（按换行、逗号或空格分割）
    const addressList = bulkAddresses
      .split(/[\n,\s]+/)
      .map(addr => addr.trim())
      .filter(addr => addr && addr.startsWith('0x'));
    
    if (addressList.length === 0) {
      alert('未找到有效的钱包地址');
      return;
    }

    // 为每个地址创建一个接收者对象，使用统一金额
    const newRecipients = addressList.map(address => ({
      address,
      amount: uniformAmount
    }));

    setRecipients(newRecipients);
    alert(`已成功添加 ${newRecipients.length} 个接收地址`);
  };

  // 设置所有接收者使用统一金额
  const applyUniformAmount = () => {
    if (!uniformAmount.trim()) {
      alert('请输入统一金额');
      return;
    }

    const newRecipients = recipients.map(recipient => ({
      ...recipient,
      amount: uniformAmount
    }));

    setRecipients(newRecipients);
  };

  // 执行批量转账
  const executeBatchTransfer = async () => {
    if (recipients.length === 0) {
      alert('请先添加接收地址');
      return;
    }

    // 验证所有收款地址和金额
    const invalidRecipients = recipients.filter(r => !r.address || !r.address.startsWith('0x') || !r.amount);
    if (invalidRecipients.length > 0) {
      alert(`有 ${invalidRecipients.length} 个接收者信息不完整，请检查地址和金额`);
      return;
    }

    setIsProcessing(true);
    setTxHashes([]);

    try {
      let signer;
      
      if (transferMethod === 'wallet') {
        // 钱包连接方式
        if (!window.ethereum) {
          throw new Error('请安装MetaMask或其他Web3钱包');
        }
        
        const provider = new ethers.BrowserProvider(window.ethereum);
        await provider.send("eth_requestAccounts", []);
        signer = await provider.getSigner();
      } else {
        // 私钥导入方式
        if (!privateKey) {
          throw new Error('请输入私钥');
        }
        
        const provider = new ethers.JsonRpcProvider('https://bsc-dataseed.binance.org/');
        signer = new ethers.Wallet(privateKey, provider);
      }

      const senderAddress = await signer.getAddress();
      const isBNB = tokenAddress === '' || tokenAddress === ethers.ZeroAddress;

      // 批量转账合约地址
      const BATCH_TRANSFER_CONTRACT = "0xb6C604F867243BaA6C36e4E08911fa512C2e9021";
      const batchTransferContract = new ethers.Contract(
        BATCH_TRANSFER_CONTRACT,
        [
          "function batchTransferBNB(address[] calldata recipients, uint256[] calldata amounts) external payable",
          "function batchTransferToken(address token, address[] calldata recipients, uint256[] calldata amounts) external"
        ],
        signer
      );

      if (isBNB) {
        // BNB批量转账 - 使用合约
        const provider = signer.provider;
        
        // 计算总转账金额
        const totalAmount = recipients.reduce((sum, recipient) => {
          return sum + parseFloat(recipient.amount || '0');
        }, 0);
        
        // 准备合约调用参数
        const recipientAddresses = recipients.map(r => r.address);
        const amounts = recipients.map(r => ethers.parseEther(r.amount));
        
        // 获取gas估算
        const gasEstimate = await batchTransferContract.batchTransferBNB.estimateGas(
          recipientAddresses,
          amounts,
          { value: ethers.parseEther(totalAmount.toString()) }
        );
        
        const feeData = await provider.getFeeData();
        const gasPrice = feeData.gasPrice;
        const totalGasFee = gasPrice * gasEstimate;
        const totalGasFeeEther = parseFloat(ethers.formatEther(totalGasFee));
        
        // 给用户展示确认窗口
        if (!window.confirm(`您将批量向 ${recipients.length} 个地址转账总计 ${totalAmount.toFixed(6)} BNB，预估gas费约 ${totalGasFeeEther.toFixed(6)} BNB\n\n确认执行此操作吗？`)) {
          setIsProcessing(false);
          return;
        }
        
        try {
          // 执行批量转账
          const tx = await batchTransferContract.batchTransferBNB(
            recipientAddresses,
            amounts,
            { value: ethers.parseEther(totalAmount.toString()) }
          );
          
          // 等待交易确认
          const receipt = await tx.wait();
          setTxHashes([receipt.hash]);
          
          alert(`批量转账完成！交易哈希: ${receipt.hash}`);
        } catch (error) {
          console.error('批量转账失败:', error);
          throw error;
        }
      } else {
        // 代币批量转账
        try {
          // 获取代币信息
          const tokenContract = new ethers.Contract(
            tokenAddress,
            [
              'function approve(address spender, uint256 amount) returns (bool)',
              'function allowance(address owner, address spender) view returns (uint256)',
              'function transfer(address to, uint256 amount) returns (bool)',
              'function transferFrom(address sender, address recipient, uint256 amount) returns (bool)',
              'function balanceOf(address owner) view returns (uint256)',
              'function decimals() view returns (uint8)',
              'function symbol() view returns (string)'
            ],
            signer
          );
          
          // 获取代币信息
          const decimals = await tokenContract.decimals();
          const symbol = await tokenContract.symbol();
          
          // 准备合约调用参数
          const recipientAddresses = recipients.map(r => r.address);
          const amounts = recipients.map(r => ethers.parseUnits(r.amount, decimals));
          
          // 计算总金额
          const totalAmount = amounts.reduce((a, b) => a + b, ethers.toBigInt(0));
          
          // 检查余额
          const balance = await tokenContract.balanceOf(senderAddress);
          if (balance < totalAmount) {
            throw new Error(`代币余额不足，需要 ${ethers.formatUnits(totalAmount, decimals)} ${symbol}，但只有 ${ethers.formatUnits(balance, decimals)} ${symbol}`);
          }

          // 检查授权额度
          const allowance = await tokenContract.allowance(senderAddress, BATCH_TRANSFER_CONTRACT);
          if (allowance < totalAmount) {
            // 给用户展示授权确认窗口
            if (!window.confirm(`需要授权合约使用您的 ${symbol} 代币。\n\n授权金额: ${ethers.formatUnits(totalAmount, decimals)} ${symbol}\n\n是否继续？`)) {
              setIsProcessing(false);
              return;
            }
            
            try {
              // 执行授权
              const approveTx = await tokenContract.approve(BATCH_TRANSFER_CONTRACT, totalAmount);
              await approveTx.wait();
              alert('授权成功！正在执行批量转账...');
            } catch (approveError) {
              console.error('授权失败:', approveError);
              throw new Error(`授权失败: ${approveError.message}`);
            }
          }
          
          // 给用户展示确认窗口
          if (!window.confirm(`您将批量向 ${recipients.length} 个地址转账总计 ${ethers.formatUnits(totalAmount, decimals)} ${symbol}\n\n确认执行此操作吗？`)) {
            setIsProcessing(false);
            return;
          }
          
          // 执行批量转账
          const tx = await batchTransferContract.batchTransferToken(
            tokenAddress,
            recipientAddresses,
            amounts
          );
          
          // 等待交易确认
          const receipt = await tx.wait();
          setTxHashes([receipt.hash]);
          
          alert(`批量转账完成！交易哈希: ${receipt.hash}`);
        } catch (error) {
          console.error('代币转账出错:', error);
          throw error;
        }
      }
    } catch (error) {
      console.error('操作出错:', error);
      alert(`操作失败: ${error.message}`);
    } finally {
      setIsProcessing(false);
    }
  };

  // 执行代币归集
  const executeTokenSweep = async () => {
    if (!privateKeys || !destinationAddress) {
      alert('请填写所有必填字段');
      return;
    }

    setIsProcessing(true);
    setTxHashes([]);
    setSweepResults([]); // 清空之前的归集结果

    try {
      const keys = privateKeys.split('\n').filter(key => key.trim() !== '');
      const provider = new ethers.JsonRpcProvider('https://bsc-dataseed.binance.org/');
      const isBNB = tokenAddress === '' || tokenAddress === ethers.ZeroAddress;
      
      // 如果设置了归集数量，转换为BigInt类型
      const specificAmount = sweepAmount.trim() !== '' 
        ? (isBNB ? ethers.parseEther(sweepAmount) : ethers.parseUnits(sweepAmount, 18))
        : null;
      
      const hashes = [];
      const results = [];
      
      // 批量处理，每批次处理5个钱包
      const batchSize = 5;
      const totalBatches = Math.ceil(keys.length / batchSize);
      
      for (let batchIndex = 0; batchIndex < totalBatches; batchIndex++) {
        const batchStart = batchIndex * batchSize;
        const batchEnd = Math.min(batchStart + batchSize, keys.length);
        const currentBatchKeys = keys.slice(batchStart, batchEnd);
        
        // 并行处理当前批次的钱包
        const batchPromises = currentBatchKeys.map(async (key) => {
          try {
            const wallet = new ethers.Wallet(key.trim(), provider);
            const walletAddress = wallet.address;
            
            // 初始化结果对象
            const resultItem = {
              address: walletAddress,
              status: '处理中',
              amount: '0',
              txHash: '',
              error: '',
              timestamp: new Date().toLocaleString()
            };
            
            // 添加到结果数组
            results.push(resultItem);
            
            if (isBNB) {
              // 归集BNB
              const balance = await provider.getBalance(walletAddress);
              const feeData = await provider.getFeeData();
              const gasPrice = feeData.gasPrice;
              const gasLimit = 21000; // 标准转账gas limit
              
              // 计算gas费用
              const gasFee = gasPrice * ethers.toBigInt(gasLimit);
              
              // 确定转账金额
              let amount;
              
              if (specificAmount && balance >= specificAmount + gasFee) {
                // 余额足够支付指定金额和gas费
                amount = specificAmount;
                resultItem.amount = ethers.formatEther(amount) + ' BNB';
              } else if (!specificAmount && balance > gasFee) {
                // 未指定金额，转出所有余额减去gas费
                amount = balance - gasFee;
                resultItem.amount = ethers.formatEther(amount) + ' BNB';
              } else {
                // 余额不足
                resultItem.status = '失败';
                resultItem.error = '余额不足';
                resultItem.amount = ethers.formatEther(balance) + ' BNB';
                return resultItem;
              }
              
              try {
                const tx = await wallet.sendTransaction({
                  to: destinationAddress,
                  value: amount,
                  gasPrice: gasPrice,
                  gasLimit: gasLimit
                });
                
                hashes.push(tx.hash);
                resultItem.txHash = tx.hash;
                resultItem.status = '已提交';
                
                // 不等待交易确认，只检查交易是否提交成功
                resultItem.status = '成功';
                return resultItem;
              } catch (txError) {
                resultItem.status = '失败';
                resultItem.error = txError.message.slice(0, 50) + '...';
                return resultItem;
              }
            } else {
              // 归集代币
              try {
                const tokenContract = new ethers.Contract(
                  tokenAddress,
                  [
                    'function balanceOf(address owner) view returns (uint256)',
                    'function decimals() view returns (uint8)',
                    'function symbol() view returns (string)',
                    'function transfer(address to, uint256 amount) returns (bool)'
                  ],
                  wallet
                );
                
                // 尝试获取代币信息
                let decimals = 18;
                let symbol = '';
                try {
                  decimals = await tokenContract.decimals();
                  symbol = await tokenContract.symbol();
                } catch (infoError) {
                  console.warn('无法获取代币信息:', infoError);
                }
                
                const balance = await tokenContract.balanceOf(walletAddress);
                
                // 确定转账金额
                let transferAmount;
                
                if (specificAmount && balance >= specificAmount) {
                  // 余额足够，转出指定金额
                  transferAmount = specificAmount;
                  resultItem.amount = ethers.formatUnits(transferAmount, decimals) + (symbol ? ` ${symbol}` : '');
                } else if (!specificAmount && balance > ethers.toBigInt(0)) {
                  // 未指定金额，转出所有余额
                  transferAmount = balance;
                  resultItem.amount = ethers.formatUnits(transferAmount, decimals) + (symbol ? ` ${symbol}` : '');
                } else {
                  // 余额不足
                  resultItem.status = '失败';
                  resultItem.error = '代币余额不足';
                  resultItem.amount = ethers.formatUnits(balance, decimals) + (symbol ? ` ${symbol}` : '');
                  return resultItem;
                }
                
                if (transferAmount <= ethers.toBigInt(0)) {
                  resultItem.status = '失败';
                  resultItem.error = '代币余额为0';
                  return resultItem;
                }
                
                // 转账代币
                const tx = await tokenContract.transfer(destinationAddress, transferAmount);
                hashes.push(tx.hash);
                resultItem.txHash = tx.hash;
                resultItem.status = '已提交';
                
                // 不等待交易确认，只检查交易是否提交成功
                resultItem.status = '成功';
                return resultItem;
              } catch (tokenError) {
                resultItem.status = '失败';
                resultItem.error = tokenError.message.slice(0, 50) + '...';
                return resultItem;
              }
            }
          } catch (error) {
            // 处理整体错误
            console.error(`处理钱包时出错: ${error.message}`);
            return {
              address: '未知地址',
              status: '失败',
              amount: '0',
              txHash: '',
              error: error.message.slice(0, 50) + '...',
              timestamp: new Date().toLocaleString()
            };
          }
        });
        
        // 等待当前批次的所有操作完成
        const batchResults = await Promise.all(batchPromises);
        
        // 更新UI显示当前进度
        setSweepResults([...results]);
        setTxHashes([...hashes]);
      }
      
      // 根据成功的交易数量显示不同的消息
      const successCount = results.filter(r => r.status === '成功').length;
      if (successCount > 0) {
        alert(`归集完成，成功处理 ${successCount} 个钱包，失败 ${results.length - successCount} 个钱包`);
      } else {
        alert('归集完成，但没有成功的交易');
      }
    } catch (error) {
      console.error('归集出错:', error);
      alert(`归集失败: ${error.message}`);
    } finally {
      setIsProcessing(false);
    }
  };

  // 批量创建钱包
  const createWallets = async () => {
    const count = parseInt(walletCount) || 0;
    if (count <= 0 || count > 100) {
      alert('请输入有效的钱包数量（1-100之间）');
      return;
    }

    setIsProcessing(true);
    
    try {
      // 使用 Promise.all 并行创建钱包
      const walletPromises = Array(count).fill().map(() => {
        return new Promise((resolve) => {
          const mnemonic = ethers.Wallet.createRandom().mnemonic;
          const wallet = ethers.Wallet.fromPhrase(mnemonic.phrase);
          resolve({
            address: wallet.address,
            mnemonic: mnemonic.phrase,
            privateKey: wallet.privateKey
          });
        });
      });

      const newWallets = await Promise.all(walletPromises);
      setCreatedWallets(newWallets);
    } catch (error) {
      console.error('创建钱包出错:', error);
      alert(`创建钱包失败: ${error.message}`);
    } finally {
      setIsProcessing(false);
    }
  };

  // 下载钱包CSV
  const downloadWalletsCSV = () => {
    if (createdWallets.length === 0) {
      alert('没有可下载的钱包');
      return;
    }

    const headers = ['地址', '助记词', '私钥'];
    const csvContent = [
      headers.join(','),
      ...createdWallets.map(wallet => [
        `"${wallet.address}"`,
        `"${wallet.mnemonic}"`,
        `"${wallet.privateKey}"`
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `钱包列表_${new Date().getTime()}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // 查询代币信息
  const fetchTokenInfo = async (address) => {
    if (!address || address.trim() === '' || address === ethers.ZeroAddress) {
      setTokenInfo({ name: 'BNB', symbol: 'BNB', decimals: 18 });
      return;
    }
    
    try {
      // 验证地址格式
      if (!address.startsWith('0x') || address.length !== 42) {
        setTokenInfo(null);
        return;
      }
      
      const provider = new ethers.JsonRpcProvider('https://bsc-dataseed.binance.org/');
      const tokenContract = new ethers.Contract(
        address,
        [
          'function name() view returns (string)',
          'function symbol() view returns (string)',
          'function decimals() view returns (uint8)'
        ],
        provider
      );
      
      const [name, symbol, decimals] = await Promise.all([
        tokenContract.name().catch(() => '未知代币'),
        tokenContract.symbol().catch(() => '???'),
        tokenContract.decimals().catch(() => 18)
      ]);
      
      setTokenInfo({ name, symbol, decimals });
    } catch (error) {
      console.error('获取代币信息出错:', error);
      setTokenInfo(null);
    }
  };

  // 处理代币地址变更
  const handleTokenAddressChange = (e) => {
    const address = e.target.value;
    setTokenAddress(address);
    // 如果地址长度为42（一个有效的以太坊地址长度），尝试获取代币信息
    if (address.length === 42 || address.trim() === '') {
      fetchTokenInfo(address);
    } else {
      setTokenInfo(null);
    }
  };

  return (
    <div style={{ maxWidth: '900px', margin: '0 auto', padding: '20px' }}>
      <h1>代币批量操作工具</h1>
      
      <div style={{ marginBottom: '20px' }}>
        <button 
          onClick={() => setMode('transfer')} 
          style={{ marginRight: '10px', fontWeight: mode === 'transfer' ? 'bold' : 'normal', padding: '8px 16px' }}
        >
          批量转账
        </button>
        <button 
          onClick={() => setMode('sweep')}
          style={{ marginRight: '10px', fontWeight: mode === 'sweep' ? 'bold' : 'normal', padding: '8px 16px' }}
        >
          代币归集
        </button>
        <button 
          onClick={() => setMode('create')}
          style={{ fontWeight: mode === 'create' ? 'bold' : 'normal', padding: '8px 16px' }}
        >
          批量创建钱包
        </button>
      </div>
      
      {(mode === 'transfer' || mode === 'sweep') && (
        <div style={{ marginBottom: '20px' }}>
          <label>代币合约地址（留空为BNB）:</label>
          <input 
            type="text" 
            value={tokenAddress} 
            onChange={handleTokenAddressChange} 
            placeholder="0x..."
            style={{ width: '100%', padding: '8px' }}
          />
          {tokenInfo ? (
            <div style={{ 
              marginTop: '5px', 
              padding: '8px', 
              backgroundColor: '#e3f2fd', 
              borderRadius: '4px',
              display: 'flex',
              alignItems: 'center',
              gap: '10px'
            }}>
              <span style={{ fontWeight: 'bold' }}>当前代币:</span>
              <span>{tokenInfo.name} ({tokenInfo.symbol})</span>
              <span style={{ marginLeft: 'auto', fontSize: '12px', color: '#666' }}>
                小数位数: {tokenInfo.decimals}
              </span>
            </div>
          ) : tokenAddress.length === 42 ? (
            <div style={{ 
              marginTop: '5px', 
              padding: '8px', 
              backgroundColor: '#ffebee', 
              borderRadius: '4px',
              color: '#d32f2f'
            }}>
              无法识别的代币地址，请检查合约地址是否正确
            </div>
          ) : null}
        </div>
      )}
      
      {mode === 'transfer' ? (
        <div>
          <h2>批量转账</h2>
          
          <div style={{ marginBottom: '20px' }}>
            <label>转账方式:</label>
            <div>
              <label>
                <input 
                  type="radio" 
                  checked={transferMethod === 'wallet'} 
                  onChange={() => setTransferMethod('wallet')} 
                />
                连接钱包
              </label>
              <label style={{ marginLeft: '20px' }}>
                <input 
                  type="radio" 
                  checked={transferMethod === 'privateKey'} 
                  onChange={() => setTransferMethod('privateKey')} 
                />
                导入私钥
              </label>
            </div>
          </div>
          
          {transferMethod === 'privateKey' && (
            <div style={{ marginBottom: '20px' }}>
              <label>发送钱包私钥:</label>
              <input 
                type="password" 
                value={privateKey} 
                onChange={(e) => setPrivateKey(e.target.value)} 
                placeholder="输入私钥"
                style={{ width: '100%', padding: '8px' }}
              />
            </div>
          )}
          
          <div style={{ marginBottom: '20px', border: '1px solid #ddd', padding: '15px', borderRadius: '5px' }}>
            <h3 style={{ marginTop: '0' }}>批量添加接收地址</h3>
            <div style={{ marginBottom: '15px' }}>
              <label>粘贴多个钱包地址（每行一个或用逗号、空格分隔）:</label>
              <textarea 
                value={bulkAddresses} 
                onChange={(e) => setBulkAddresses(e.target.value)} 
                rows="5"
                placeholder="0x...\n0x...\n0x..."
                style={{ width: '100%', padding: '8px', marginTop: '5px' }}
              />
            </div>
            
            <div style={{ marginBottom: '15px', display: 'flex', gap: '10px', alignItems: 'flex-end' }}>
              <div style={{ flex: '1' }}>
                <label>统一转账金额:</label>
                <input 
                  type="text" 
                  value={uniformAmount} 
                  onChange={(e) => setUniformAmount(e.target.value)} 
                  placeholder="例如: 0.1"
                  style={{ width: '100%', padding: '8px', marginTop: '5px' }}
                />
              </div>
              <button 
                onClick={processBulkAddresses}
                style={{ padding: '8px 16px', backgroundColor: '#2196F3', color: 'white', height: '36px' }}
              >
                批量添加地址
              </button>
              
              {recipients.length > 0 && (
                <button 
                  onClick={applyUniformAmount}
                  style={{ padding: '8px 16px', backgroundColor: '#FF9800', color: 'white', height: '36px' }}
                >
                  应用统一金额
                </button>
              )}
            </div>
          </div>
          
          <h3>接收者列表:</h3>
          <div style={{ maxHeight: '300px', overflowY: 'auto', marginBottom: '15px', border: '1px solid #eee', padding: '10px' }}>
            {recipients.length > 0 ? (
              recipients.map((recipient, index) => (
                <div key={index} style={{ marginBottom: '10px', display: 'flex', gap: '10px' }}>
                  <input
                    type="text"
                    value={recipient.address}
                    onChange={(e) => updateRecipient(index, 'address', e.target.value)}
                    placeholder="接收地址 0x..."
                    style={{ flex: 2, padding: '8px' }}
                  />
                  <input
                    type="text"
                    value={recipient.amount}
                    onChange={(e) => updateRecipient(index, 'amount', e.target.value)}
                    placeholder="金额"
                    style={{ flex: 1, padding: '8px' }}
                  />
                </div>
              ))
            ) : (
              <div style={{ padding: '15px', textAlign: 'center', color: '#666' }}>
                暂无接收者，请添加接收地址或使用批量添加功能
              </div>
            )}
          </div>
          
          <div style={{ marginBottom: '20px', display: 'flex', gap: '10px' }}>
            <button onClick={addRecipient} style={{ padding: '8px 16px' }}>
              添加接收者
            </button>
            <button 
              onClick={() => setRecipients([])}
              style={{ padding: '8px 16px', backgroundColor: '#f44336', color: 'white' }}
            >
              清空列表
            </button>
            <button 
              onClick={executeBatchTransfer} 
              disabled={isProcessing || recipients.length === 0}
              style={{ 
                padding: '8px 16px', 
                backgroundColor: isProcessing ? '#ccc' : recipients.length === 0 ? '#a5d6a7' : '#4CAF50', 
                color: 'white' 
              }}
            >
              {isProcessing ? '处理中...' : `执行批量转账 (${recipients.length}个地址)`}
            </button>
          </div>
        </div>
      ) : mode === 'sweep' ? (
        <div>
          <h2>代币归集</h2>
          
          <div style={{ marginBottom: '20px' }}>
            <label>钱包私钥列表（每行一个）:</label>
            <textarea 
              value={privateKeys} 
              onChange={(e) => setPrivateKeys(e.target.value)} 
              rows="5"
              placeholder="每行输入一个私钥"
              style={{ width: '100%', padding: '8px' }}
            />
          </div>
          
          <div style={{ marginBottom: '20px' }}>
            <label>归集目标地址:</label>
            <input 
              type="text" 
              value={destinationAddress} 
              onChange={(e) => setDestinationAddress(e.target.value)} 
              placeholder="0x..."
              style={{ width: '100%', padding: '8px' }}
            />
          </div>
          
          <div style={{ marginBottom: '20px' }}>
            <label>归集数量（留空则全部归集）:</label>
            <input 
              type="text" 
              value={sweepAmount} 
              onChange={(e) => setSweepAmount(e.target.value)} 
              placeholder="例如: 0.1"
              style={{ width: '100%', padding: '8px' }}
            />
          </div>
          
          <div style={{ marginBottom: '20px', display: 'flex', gap: '10px' }}>
            <button 
              onClick={executeTokenSweep} 
              disabled={isProcessing}
              style={{ padding: '8px 16px', backgroundColor: isProcessing ? '#ccc' : '#4CAF50', color: 'white' }}
            >
              {isProcessing ? '归集中...' : '开始归集'}
            </button>
            
            {sweepResults.length > 0 && !isProcessing && (
              <button 
                onClick={() => {
                  // 生成CSV文件下载
                  const headers = ['钱包地址', '状态', '金额', '交易哈希', '错误信息', '时间'];
                  const csvContent = [
                    headers.join(','),
                    ...sweepResults.map(result => [
                      `"${result.address}"`,
                      `"${result.status}"`,
                      `"${result.amount}"`,
                      `"${result.txHash}"`,
                      `"${result.error}"`,
                      `"${result.timestamp}"`
                    ].join(','))
                  ].join('\n');
                  
                  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
                  const url = URL.createObjectURL(blob);
                  const link = document.createElement('a');
                  link.setAttribute('href', url);
                  link.setAttribute('download', `归集结果_${new Date().getTime()}.csv`);
                  link.style.visibility = 'hidden';
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
                }}
                style={{ padding: '8px 16px', backgroundColor: '#2196F3', color: 'white' }}
              >
                下载结果CSV
              </button>
            )}
          </div>
          
          {sweepResults.length > 0 && (
            <div style={{ marginTop: '20px', overflowX: 'auto' }}>
              <h3>归集结果:</h3>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left' }}>钱包地址</th>
                    <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left' }}>状态</th>
                    <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left' }}>金额</th>
                    <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left' }}>交易哈希</th>
                    <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left' }}>错误信息</th>
                    <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left' }}>时间</th>
                  </tr>
                </thead>
                <tbody>
                  {sweepResults.map((result, index) => (
                    <tr key={index} style={{ backgroundColor: 
                      result.status === '成功' ? '#e8f5e9' : 
                      result.status === '处理中' ? '#e3f2fd' : 
                      result.status === '已提交' ? '#fff9c4' : '#ffebee' 
                    }}>
                      <td style={{ border: '1px solid #ddd', padding: '8px', wordBreak: 'break-all' }}>
                        {result.address}
                      </td>
                      <td style={{ border: '1px solid #ddd', padding: '8px' }}>
                        {result.status}
                      </td>
                      <td style={{ border: '1px solid #ddd', padding: '8px' }}>
                        {result.amount}
                      </td>
                      <td style={{ border: '1px solid #ddd', padding: '8px', wordBreak: 'break-all' }}>
                        {result.txHash ? (
                          <a 
                            href={`https://bscscan.com/tx/${result.txHash}`} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            style={{ color: '#1976d2', textDecoration: 'underline' }}
                          >
                            {result.txHash.slice(0, 10)}...
                          </a>
                        ) : ''}
                      </td>
                      <td style={{ border: '1px solid #ddd', padding: '8px', color: result.error ? '#f44336' : '' }}>
                        {result.error}
                      </td>
                      <td style={{ border: '1px solid #ddd', padding: '8px' }}>
                        {result.timestamp}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      ) : (
        <div>
          <h2>批量创建钱包</h2>
          
          <div style={{ marginBottom: '20px' }}>
            <label>创建数量 (最多100个):</label>
            <input 
              type="number" 
              min="1"
              max="100"
              value={walletCount} 
              onChange={(e) => setWalletCount(e.target.value)} 
              style={{ width: '100%', padding: '8px' }}
            />
          </div>
          
          <div style={{ marginBottom: '20px', display: 'flex', gap: '10px' }}>
            <button 
              onClick={createWallets} 
              disabled={isProcessing}
              style={{ padding: '8px 16px', backgroundColor: isProcessing ? '#ccc' : '#4CAF50', color: 'white' }}
            >
              {isProcessing ? '创建中...' : '创建钱包'}
            </button>
            
            {createdWallets.length > 0 && (
              <button 
                onClick={downloadWalletsCSV}
                style={{ padding: '8px 16px', backgroundColor: '#2196F3', color: 'white' }}
              >
                下载CSV
              </button>
            )}
          </div>
          
          {createdWallets.length > 0 && (
            <div style={{ marginTop: '20px', overflowX: 'auto' }}>
              <h3>创建的钱包列表:</h3>
              <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead style={{ position: 'sticky', top: 0, backgroundColor: '#fff', zIndex: 1 }}>
                    <tr>
                      <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left' }}>序号</th>
                      <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left' }}>地址</th>
                      <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left' }}>助记词</th>
                      <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left' }}>私钥</th>
                    </tr>
                  </thead>
                  <tbody>
                    {createdWallets.map((wallet, index) => (
                      <tr key={index}>
                        <td style={{ border: '1px solid #ddd', padding: '8px' }}>{index + 1}</td>
                        <td style={{ border: '1px solid #ddd', padding: '8px', wordBreak: 'break-all' }}>
                          {wallet.address}
                        </td>
                        <td style={{ border: '1px solid #ddd', padding: '8px', wordBreak: 'break-all' }}>
                          {wallet.mnemonic}
                        </td>
                        <td style={{ border: '1px solid #ddd', padding: '8px', wordBreak: 'break-all' }}>
                          {wallet.privateKey}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}
      
      {txHashes.length > 0 && (
        <div style={{ marginTop: '20px' }}>
          <h3>交易哈希:</h3>
          <ul>
            {txHashes.map((hash, index) => (
              <li key={index}>
                <a 
                  href={`https://bscscan.com/tx/${hash}`} 
                  target="_blank" 
                  rel="noopener noreferrer"
                >
                  {hash}
                </a>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default TokenBatchTool;